import React, { useEffect, useState } from "react";
import MainLayout from "../../../layouts/MainLayout";
import "../../Incident/IncidentRegister/incidentRegister.css";
import {
  Asset,
  AssetReferralActionStatusEnum,
  CrimeReference,
  ProhibitionRequest,
  Referral,
  Report,
  Roles,
} from "types";
import "./AssetReferralRegisterCss.css";
import AssetRegister from "../Assets/AddAsset";
import AssetReferralRegisterContainer from "./AssetReferralRegisterContainer";
import AssetList from "../Assets/AssetList";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchAssetReferralById,
  getAssetRecoveryReportsByReferralId,
  getReferralReportsByReferralId,
} from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import DecisionPopup from "../Descision/ReferralDecision/AddCordinatorDecision";
import ReferralDecision from "../Descision/ReferralDecision/ReferralDecision";
import { useAuth } from "context/AuthContext";
import { hasRole } from "utils/helper";
import ProsecutorDecisionPopup from "../Descision/ReferralDecision/AddProsecutorDecision";
import ReportContainer from "../Report/Prohibition/ProhibitionReportContainer";
import { AddAssetRecoveryPopup } from "../AssetRecovery/AddAssetRecovery";
import CreateTerminationRequestPopup from "../Termination/AddTerminationRequest";
import AssetRecoveryReportContainer from "../Report/AssetRecovery/AssetRecoveryReportContainer";
import AssetReferralInvestigation from "./AssetReferralInvestigation";
import AssetReferralProhibition from "./AssetReferralProhibition";

const tabs = [
  "Referral",
  // "Assets",
  "Prohibition",
  "Investigation",
  "Decision",
  "Prohibition Reports",
  "Recovery Reports",
];

type Props = {
  incident?: CrimeReference;
};

const AssetRecoveryRegistration: React.FC<Props> = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [showAddAssetForm, setShowAddAssetForm] = useState(false);
  const [showAddReferralForm, setShowAddReferralForm] = useState(false);
  const [isDecisionPopupOpen, setIsDecisionPopupOpen] = useState(false);
  const [assetRecoveryFiles, setAssetRecoveryFiles] = useState<File[]>([]);
  const [isAssetDecisionPopupOpen, setIsAssetDecisionPopupOpen] = useState<{
    data: any;
    open: boolean;
  }>({ data: null, open: false });
  const [
    isAssetRecoveryDecisionPopupOpen,
    setIsAssetRecoveryDecisionPopupOpen,
  ] = useState<{ data: any; open: boolean }>({ data: null, open: false });
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);

  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [referralData, setReferralData] = useState<Referral | null>(null);
  const [loading, setLoading] = useState(false);
  const { showTooltip } = useTooltip();
  const navigate = useNavigate();
  const isReferralIdExists = !!id;
  const [reports, setReports] = useState<Report[]>([]);
  const [recoveryReports, setRecoveryReports] = useState<Report[]>([]);
  const [isTerminationRequestPopupOpen, setIsTerminationRequestPopupOpen] =
    useState<{
      data: any;
      open: boolean;
    }>({ data: null, open: false });
  useEffect(() => {
    if (id) {
      setLoading(true);
      fetchAssetReferralById(id)
        .then((data) => {
          setReferralData(data);
          setLoading(false);
        })
        .catch((err) => {
          showTooltip(t("failed_to_fetch_referral_data"), "error");
          setLoading(false);
        });
    } else {
      // Reset referral data when no ID (creating new)
      setReferralData(null);
    }
  }, [id]);
  useEffect(() => {
    if (referralData?.id) {
      getReferralReportsByReferralId(referralData?.id as string)
        .then((d) => setReports(d))
        .catch(console.error);
      getAssetRecoveryReportsByReferralId(referralData?.id as string)
        .then((d) => setRecoveryReports(d))
        .catch(console.error);
    }
  }, [referralData?.id]);

  // Conditions for showing prohibition reports tab
  const shouldShowProhibitionReportsTab =
    isReferralIdExists &&
    referralData != null &&
    // Check if any prohibition record has status PROHIBITION_ON_LITIGATION or PROHIBITION_FINAL_VERDICT
    (referralData.prohibition_record?.some(
      (record: ProhibitionRequest) =>
        record.status ===
          AssetReferralActionStatusEnum.PROHIBITION_ON_LITIGATION ||
        record.status ===
          AssetReferralActionStatusEnum.PROHIBITION_FINAL_VERDICT,
    ) ||
      // Keep existing conditions for other statuses
      [
        AssetReferralActionStatusEnum.ASSET_RECOVERY_REQUESTED,
        AssetReferralActionStatusEnum.ASSET_RECOVERY_REQUEST_APPROVED,
        AssetReferralActionStatusEnum.ASSET_RECOVERY_ON_REVISION,
        AssetReferralActionStatusEnum.ASSET_RECOVERY_UPDATED,
        AssetReferralActionStatusEnum.ASSET_RECOVERY_ON_LITIGATION,
        AssetReferralActionStatusEnum.ASSET_RECOVERY_FINAL_VERDICT,
      ].includes(referralData.action_status as AssetReferralActionStatusEnum) ||
      (reports.length > 0 &&
        [
          AssetReferralActionStatusEnum.TERMINATION_REQUESTED,
          AssetReferralActionStatusEnum.TERMINATED_BY_PROSECUTOR,
          AssetReferralActionStatusEnum.TERMINATION_REJECTED,
          AssetReferralActionStatusEnum.SUSPENSION_REQUESTED,
          AssetReferralActionStatusEnum.SUSPENDED,
          AssetReferralActionStatusEnum.SUSPENSION_REJECTED,
          AssetReferralActionStatusEnum.REINITIATE_REQUESTED,
          AssetReferralActionStatusEnum.REINITIATE_REJECTED,
          AssetReferralActionStatusEnum.REINITIATE_APPROVED,
        ].includes(
          referralData.action_status as AssetReferralActionStatusEnum,
        )));

  // Conditions for showing recovery reports tab
  const shouldShowRecoveryReportsTab =
    isReferralIdExists &&
    referralData?.action_status != null &&
    ([
      AssetReferralActionStatusEnum.ASSET_RECOVERY_ON_LITIGATION,
      AssetReferralActionStatusEnum.ASSET_RECOVERY_FINAL_VERDICT,
      AssetReferralActionStatusEnum.COURT_CASE_FILED,
      AssetReferralActionStatusEnum.EXECUTION_IN_PROGRESS,
      AssetReferralActionStatusEnum.RECOVERED,
    ].includes(referralData.action_status as AssetReferralActionStatusEnum) ||
      (recoveryReports.length > 0 &&
        [
          AssetReferralActionStatusEnum.TERMINATION_REQUESTED,
          AssetReferralActionStatusEnum.TERMINATED_BY_PROSECUTOR,
          AssetReferralActionStatusEnum.TERMINATION_REJECTED,
          AssetReferralActionStatusEnum.SUSPENSION_REQUESTED,
          AssetReferralActionStatusEnum.SUSPENDED,
          AssetReferralActionStatusEnum.SUSPENSION_REJECTED,
          AssetReferralActionStatusEnum.REINITIATE_REQUESTED,
          AssetReferralActionStatusEnum.REINITIATE_REJECTED,
          AssetReferralActionStatusEnum.REINITIATE_APPROVED,
        ].includes(
          referralData.action_status as AssetReferralActionStatusEnum,
        )));

  const handleReferralCreated = (newReferralId: string) => {
    navigate(`/asset-recovery/${newReferralId}`);
  };

  const handleTabSelect = (tab: string) => {
    // Only allow switching to Assets, Decision, and Reports tabs if referral ID exists
    if (
      (tab === "Investigation" ||
        tab === "Prohibition" ||
        tab === "Decision" ||
        tab === "Prohibition Reports" ||
        tab === "Recovery Reports") &&
      !isReferralIdExists
    ) {
      return;
    }

    // For Prohibition Reports tab, additional check for specific status
    if (tab === "Prohibition Reports" && !shouldShowProhibitionReportsTab) {
      return;
    }

    // For Recovery Reports tab, additional check for specific status
    if (tab === "Recovery Reports" && !shouldShowRecoveryReportsTab) {
      return;
    }

    setActiveTab(tab);
  };

  const handleAddDecisionClick = () => {
    setIsDecisionPopupOpen(true);
  };

  const handleCreateTerminationRequestClick = () => {
    console.log("Creating Termination Request");
    if (
      referralData?.termination_reason ||
      referralData?.termination_date
      // referralData?.termination_order_documents
    ) {
      setIsTerminationRequestPopupOpen({
        data: {
          termination_reason: referralData?.termination_reason,
          termination_date: referralData?.termination_date,
          termination_order_document: referralData?.termination_order_documents,
        },
        open: true,
      });
    } else setIsTerminationRequestPopupOpen({ data: null, open: true });
  };
  const handleCloseTerminationRequestPopup = () => {
    setIsTerminationRequestPopupOpen({ data: null, open: false });
  };
  const handleCloseDecisionPopup = () => {
    setIsDecisionPopupOpen(false);
  };
  const hasInvestigationData =
    Boolean(
      referralData?.investigation_summary &&
      referralData?.investigation_summary.trim() !== "",
    ) || referralData?.investigation_summary_file;
  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
    // setIsUserDetailsOpen:setIsUserDetailsOpen(false)
  };
  const handleAssetCreated = async (data?: Referral | Asset) => {
    if (id) {
      try {
        // Refetch the referral data to get the updated assets list
        const updatedData = await fetchAssetReferralById(id);
        setReferralData(updatedData);
      } catch (error) {
        console.error("Failed to refresh referral data:", error);
        showTooltip(t("asset_created_but_failed_to_refresh_data"), "info");
      }
    }
  };

  const handleAddAssetDecisionClick = () => {
    if (
      referralData?.prohabition_request_summary ||
      referralData?.prohabition_request_date
      // referralData?.prohibition_request_documents
    ) {
      setIsAssetDecisionPopupOpen({
        data: {
          prohabition_request_summary:
            referralData?.prohabition_request_summary,
          prohabition_request_date: referralData?.prohabition_request_date,
          prohibition_request_documents:
            referralData?.prohibition_request_documents,
        },
        open: true,
      });
    } else setIsAssetDecisionPopupOpen({ data: null, open: true });
  };

  const handleAddAssetRecoveryDecisionClick = () => {
    if (
      referralData?.asset_recovery_request_date ||
      referralData?.prosecutor_decision_on_asset_recovery ||
      // referralData?.asset_recovery_request_documents ||
      referralData?.asset_reovery_charge
    ) {
      setIsAssetRecoveryDecisionPopupOpen({
        data: {
          asset_recovery_request_date:
            referralData?.asset_recovery_request_date,
          prosecutor_decision_on_asset_recovery:
            referralData?.prosecutor_decision_on_asset_recovery,
          asset_recovery_request_documents:
            referralData?.asset_recovery_request_documents,
          asset_reovery_charge: referralData?.asset_reovery_charge,
        },
        open: true,
      });
    } else setIsAssetRecoveryDecisionPopupOpen({ data: null, open: true });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "Referral":
        if (showAddReferralForm) {
          return (
            <AssetReferralRegisterContainer
              referralId={""}
              referral={referralData}
              onReferralCreated={handleReferralCreated}
              setReferralData={setReferralData}
              setActiveTab={setActiveTab}
              onNewDataAdded={handleAssetCreated}
              handleCreateTerminationRequestClick={
                handleCreateTerminationRequestClick
              }
              handleAddAssetRecoveryDecisionClick={
                handleAddAssetRecoveryDecisionClick
              }
              handleAddAssetDecisionClick={handleAddAssetDecisionClick}
              recoveryReports={recoveryReports}
              reports={reports}
              handleAssetCreated={handleAssetCreated}
            />
          );
        }
        if (isReferralIdExists) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-md">
              <AssetReferralRegisterContainer
                referralId={id}
                referral={referralData}
                onReferralCreated={handleReferralCreated}
                setReferralData={setReferralData}
                setActiveTab={setActiveTab}
                onNewDataAdded={handleAssetCreated}
                handleCreateTerminationRequestClick={
                  handleCreateTerminationRequestClick
                }
                handleAddAssetRecoveryDecisionClick={
                  handleAddAssetRecoveryDecisionClick
                }
                handleAddAssetDecisionClick={handleAddAssetDecisionClick}
                recoveryReports={recoveryReports}
                reports={reports}
                handleAssetCreated={handleAssetCreated}
              />
            </div>
          );
        }
        return (
          <AssetReferralRegisterContainer
            referralId={undefined}
            referral={null}
            onReferralCreated={handleReferralCreated}
            recoveryReports={recoveryReports}
            reports={reports}
            handleAssetCreated={handleAssetCreated}
          />
        );
      // case "Assets":
      //   if (!isReferralIdExists) {
      //     return (
      //       <div className="p-6 bg-white rounded-lg shadow-md">
      //         <p className="text-gray-500">
      //           Please save the referral first to access assets.
      //         </p>
      //       </div>
      //     );
      //   }
      //   if (showAddAssetForm) {
      //     return (
      //       <AssetRegister
      //         referralId={id}
      //         referral={referralData}
      //         setActiveTab={setActiveTab}
      //         setShowAddAssetForm={setShowAddAssetForm}
      //         onAssetCreated={handleAssetCreated}
      //       />
      //     );
      //   } else {
      //     return (
      //       <div className="p-6 bg-white rounded-lg shadow-md">
      // <AssetList
      //   referralId={id}
      //   referral={referralData}
      //   onAssetCreated={handleAssetCreated}
      //   setActiveTab={setActiveTab}
      //   reports={reports}
      //   recoveryReports={recoveryReports}
      // />
      //       </div>
      //     );
      //   }
      case "Prohibition":
        if (!isReferralIdExists) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">
                Please save the referral first to access prohibition.
              </p>
            </div>
          );
        }

        return (
          <AssetReferralProhibition
            prohibition_records={
              referralData?.prohibition_record as ProhibitionRequest[]
            }
            handleAssetCreated={handleAssetCreated}
            setActiveTab={setActiveTab}
            referralId={id}
            referralData={referralData as Referral}
            isAssetCoordinator={isAssetCoordinator}
            isAssetProsecutor={isAssetProsecutor}
            isAssetRegistrar={isAssetRegistrar}
            onNewDataAdded={handleAssetCreated}
            setReferralData={setReferralData}
            fileRowProps={fileRowProps}
          />
        );
      case "Investigation":
        if (!isReferralIdExists) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">
                Please save the referral first to access investigation.
              </p>
            </div>
          );
        }

        return (
          <AssetReferralInvestigation
            referralId={id as string}
            referral={referralData}
            investigationData={
              hasInvestigationData
                ? {
                    investigation_summary: referralData?.investigation_summary,
                    investigation_summary_file:
                      referralData?.investigation_summary_file,
                    investigation_summary_file_details:
                      referralData?.investigation_summary_files ?? undefined,
                  }
                : undefined
            }
            isAssetCoordinator={isAssetCoordinator}
            setReferralData={setReferralData}
            onInvestigationCreated={handleAssetCreated}
            setActiveTab={setActiveTab}
            fileRowProps={fileRowProps}
          />
        );
      case "Decision":
        if (!isReferralIdExists) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">
                Please save the referral first to access decisions.
              </p>
            </div>
          );
        }
        return (
          <>
            {referralData ? (
              <ReferralDecision
                referral={referralData as Referral}
                setActiveTab={setActiveTab}
                onDecsionCreated={handleAssetCreated}
                handleAddAssetDecisionClick={handleAddAssetDecisionClick}
                handleAddAssetRecoveryDecisionClick={
                  handleAddAssetRecoveryDecisionClick
                }
                handleCreateTerminationRequestClick={
                  handleCreateTerminationRequestClick
                }
              />
            ) : (
              <p>No Decision Made</p>
            )}
          </>
        );
      case "Prohibition Reports":
        if (!isReferralIdExists) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">
                Please save the referral first to access prohibition reports.
              </p>
            </div>
          );
        }
        if (!shouldShowProhibitionReportsTab) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">
                Prohibition Reports tab is only available when referral status
                is related to prohibition or asset recovery processes.
              </p>
            </div>
          );
        }
        return (
          <ReportContainer
            referralId={referralData?.id as string}
            referral={referralData as Referral}
            setActiveTab={setActiveTab}
          />
        );
      case "Recovery Reports":
        if (!isReferralIdExists) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">
                Please save the referral first to access recovery reports.
              </p>
            </div>
          );
        }
        if (!shouldShowRecoveryReportsTab) {
          return (
            <div className="p-6 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">
                Recovery Reports tab is only available when referral status is
                related to asset recovery litigation or execution processes.
              </p>
            </div>
          );
        }
        return (
          <AssetRecoveryReportContainer
            referralId={referralData?.id as string}
            referral={referralData as Referral}
            setActiveTab={setActiveTab}
            isAssetRegistrar={isAssetRegistrar}
          />
        );

      default:
        return (
          <AssetReferralRegisterContainer
            referralId={id}
            referral={referralData}
            onReferralCreated={handleReferralCreated}
            setReferralData={setReferralData}
            onNewDataAdded={handleAssetCreated}
            handleCreateTerminationRequestClick={
              handleCreateTerminationRequestClick
            }
            handleAddAssetRecoveryDecisionClick={
              handleAddAssetRecoveryDecisionClick
            }
            handleAddAssetDecisionClick={handleAddAssetDecisionClick}
            recoveryReports={recoveryReports}
            reports={reports}
            handleAssetCreated={handleAssetCreated}
          />
        );
    }
  };

  const handleAddAssetClick = () => {
    setShowAddAssetForm(true);
  };

  const handleCancelAddAsset = () => {
    setShowAddAssetForm(false);
  };

  // Reset forms when switching tabs
  React.useEffect(() => {
    // if (activeTab !== "Assets") {
    //   setShowAddAssetForm(false);
    // }
    if (activeTab !== "Referral") {
      setShowAddReferralForm(false);
    }
  }, [activeTab]);

  const { loginData } = useAuth();
  const isAssetCoordinator = hasRole(loginData?.roles, [
    Roles.assetCoordinator,
  ]);
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const isAssetRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  // Filter tabs to only show report tabs when conditions are met
  const visibleTabs = tabs.filter((tab) => {
    if (tab === "Prohibition Reports") return shouldShowProhibitionReportsTab;
    if (tab === "Recovery Reports") return shouldShowRecoveryReportsTab;
    return true;
  });

  return (
    <MainLayout>
      <div className="incidentRegistration ">
        <div className="">
          <div className="text-2xl font-semibold text-gray-700 flex">
            <svg
              width="28"
              height="29"
              viewBox="0 0 28 29"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.33398 14.815C5.31904 15.9763 8.46917 16.6575 11.6673 16.8334V19.1666H16.334V16.8334C19.5244 16.6335 22.6687 15.9693 25.6673 14.8616V23.8334H2.33398V14.815ZM17.5006 4L18.6673 5.16665V7.5H25.6673V13.5784C22.0901 14.914 18.3088 15.6205 14.4906 15.6666H13.6506C9.78127 15.6248 5.9499 14.898 2.33398 13.52V7.5H9.33398V5.16665L10.5006 4H17.5006ZM16.334 6.33335H11.6673V7.5H16.334V6.33335Z"
                fill="#696969"
              />
            </svg>
            <p className="referral-main-title ml-3"> Asset Recovery</p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex space-x-4">
            {visibleTabs.map((tab) => {
              const isDisabled =
                // tab === "Assets" ||
                (tab === "Decision" ||
                  tab === "Prohibition Reports" ||
                  tab === "Recovery Reports") &&
                !isReferralIdExists;
              const isActive = activeTab === tab;

              return (
                <button
                  key={tab}
                  onClick={() => !isDisabled && handleTabSelect(tab)}
                  className={`px-4 py-2 rounded-t-lg transition-colors ${
                    isActive
                      ? "referral-active-tab"
                      : isDisabled
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "referral-inactive-tab hover:bg-gray-100"
                  }`}
                  disabled={isDisabled}
                >
                  {tab}
                </button>
              );
            })}
          </div>

          {/* Show Add Asset button only when on Assets tab, not showing the form, and referral ID exists */}
          {/* {activeTab === "Assets" &&
            (isAssetCoordinator || isAssetProsecutor) &&
            !showAddAssetForm &&
            isReferralIdExists && (
              <button
                className="referral-form-button text-white"
                onClick={handleAddAssetClick}
                disabled={
                  ![
                    AssetReferralActionStatusEnum.PENDING,
                    AssetReferralActionStatusEnum.INVESTIGATION_ONGOING,
                  ].includes(
                    referralData?.action_status as AssetReferralActionStatusEnum,
                  )
                }
                style={{
                  background: ![
                    AssetReferralActionStatusEnum.PENDING,
                    AssetReferralActionStatusEnum.INVESTIGATION_ONGOING,
                  ].includes(
                    referralData?.action_status as AssetReferralActionStatusEnum,
                  )
                    ? "grey"
                    : "black",
                  cursor: ![
                    AssetReferralActionStatusEnum.PENDING,
                    AssetReferralActionStatusEnum.INVESTIGATION_ONGOING,
                  ].includes(
                    referralData?.action_status as AssetReferralActionStatusEnum,
                  )
                    ? "not-allowed"
                    : "pointer",
                }}
              >
                Add Asset
              </button>
            )} */}

          {/* Show Cancel button when on Assets tab and showing the form */}
          {/* {activeTab === "Assets" && showAddAssetForm && (
            <button
              className="ml-4 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
              onClick={handleCancelAddAsset}
            >
              Cancel
            </button>
          )} */}

          {/* Show Add Decision button only when on Decision tab and referral ID exists */}
          {activeTab === "Decision" &&
            isReferralIdExists &&
            isAssetCoordinator && (
              <button
                className={`referral-form-button text-white `}
                onClick={handleAddDecisionClick}
                disabled={
                  referralData?.action_status !==
                  AssetReferralActionStatusEnum.PENDING
                }
                style={{
                  background:
                    referralData?.action_status !==
                    AssetReferralActionStatusEnum.PENDING
                      ? "grey"
                      : "black",
                  cursor:
                    referralData?.action_status !==
                    AssetReferralActionStatusEnum.PENDING
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Add Decision
              </button>
            )}
        </div>

        {/* Render the active tab content */}
        {renderTabContent()}
      </div>

      {/* Decision Popup */}
      {id && (
        <DecisionPopup
          isOpen={isDecisionPopupOpen}
          onClose={handleCloseDecisionPopup}
          referralId={id}
          onDecsionCreated={handleAssetCreated}
          setActiveTab={setActiveTab}
        />
      )}
      {id && isAssetProsecutor && (
        <ProsecutorDecisionPopup
          isOpen={isAssetDecisionPopupOpen.open}
          onClose={() =>
            setIsAssetDecisionPopupOpen({ data: null, open: false })
          }
          mode="create"
          referralId={id}
          referral={referralData}
          onDecsionCreated={handleAssetCreated}
          setActiveTab={setActiveTab}
          assetsNeedingReport={referralData?.suspect_asset_counts ?? []}
        />
      )}
      {id && isAssetProsecutor && (
        <AddAssetRecoveryPopup
          isOpen={isAssetRecoveryDecisionPopupOpen.open}
          onClose={() =>
            setIsAssetRecoveryDecisionPopupOpen({ data: null, open: false })
          }
          referralId={id}
          referral={referralData}
          onDecsionCreated={handleAssetCreated}
          setActiveTab={setActiveTab}
          setAssetRecoveryFiles={setAssetRecoveryFiles}
          assetRecoveryFiles={assetRecoveryFiles}
          existingData={{
            prosecutor_decision_on_asset_recovery:
              referralData?.prosecutor_decision_on_asset_recovery,
            asset_reovery_charge: referralData?.asset_reovery_charge,
            asset_recovery_request_date:
              referralData?.asset_recovery_request_date,
            asset_recovery_request_document:
              referralData?.asset_recovery_request_documents,
          }}
        />
      )}
      {id && isAssetProsecutor && (
        <CreateTerminationRequestPopup
          isOpen={isTerminationRequestPopupOpen.open}
          onClose={handleCloseTerminationRequestPopup}
          referralId={id}
          referral={referralData as Referral}
          onTerminationCreated={handleAssetCreated}
          setActiveTab={setActiveTab}
          existingData={isTerminationRequestPopupOpen.data}
        />
      )}
    </MainLayout>
  );
};

export default AssetRecoveryRegistration;
